import { describeError, uploadToPresignedUrl, useApi } from '@classgo/app-kit';
import type { AnnouncementAudience, AnnouncementView } from '@classgo/shared';
import { Button, Input, Select, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { listQuery } from '../../academic/api';
import { announcementsApi } from '../../announcements/api';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';

const AUDIENCES: AnnouncementAudience[] = ['all', 'sede', 'grade', 'group', 'family'];

/**
 * The composer (ticket #71): title and body, the audience picker (everyone, a sede, a grade, a
 * group, one family found through its guardian), attachments, and publish now, schedule or
 * keep as a draft. Editing a published announcement keeps its audience.
 */
export const Composer = ({
  current,
  onDone,
}: {
  current: AnnouncementView | null;
  onDone: (announcement: AnnouncementView) => void;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { sede } = useTenant();
  const [title, setTitle] = useState(current?.title ?? '');
  const [body, setBody] = useState(current?.body ?? '');
  const [audience, setAudience] = useState<AnnouncementAudience>(current?.audience ?? 'all');
  const [targetId, setTargetId] = useState(current?.targetId ?? '');
  const [familySearch, setFamilySearch] = useState('');
  const [mode, setMode] = useState<'now' | 'schedule' | 'draft'>(
    current?.status === 'scheduled' ? 'schedule' : 'draft',
  );
  const [scheduledAt, setScheduledAt] = useState(
    current?.scheduledAt ? current.scheduledAt.slice(0, 16) : '',
  );
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const sedes = useQuery(listQuery(api, 'sedes'));
  const grades = useQuery(listQuery(api, 'grades'));
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const students = useQuery({
    ...recordQueries.students(api, { search: familySearch.trim() }),
    enabled: audience === 'family' && familySearch.trim().length >= 2,
  });
  const [family, setFamily] = useState<{ studentId: string; label: string } | null>(null);
  const frozen = current?.status === 'published';
  const save = useMutation({
    mutationFn: async () => {
      const targetFor =
        audience === 'all' ? undefined : audience === 'family' ? family?.studentId : targetId;
      const scheduledIso =
        mode === 'schedule' && scheduledAt ? new Date(scheduledAt).toISOString() : undefined;
      let saved: AnnouncementView;
      if (current) {
        saved = await announcementsApi(api).update(current.id, {
          title: title.trim(),
          body: body.trim(),
          ...(frozen
            ? {}
            : { audience, targetId: targetFor ?? null, scheduledAt: scheduledIso ?? null }),
        });
        if (!frozen && mode === 'now') saved = await announcementsApi(api).publish(current.id);
      } else {
        saved = await announcementsApi(api).create({
          title: title.trim(),
          body: body.trim(),
          audience,
          ...(targetFor ? { targetId: targetFor } : {}),
          publish: files.length > 0 && mode === 'now' ? 'draft' : mode,
          ...(scheduledIso ? { scheduledAt: scheduledIso } : {}),
        });
      }
      for (const file of files) {
        const presigned = await announcementsApi(api).presignAttachment(
          file.name,
          file.type || 'application/octet-stream',
          file.size,
        );
        await uploadToPresignedUrl({
          url: presigned.url,
          file,
          contentType: file.type || 'application/octet-stream',
        });
        await announcementsApi(api).registerAttachment(saved.id, {
          key: presigned.key,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
      }
      if (!current && files.length > 0 && mode === 'now')
        saved = await announcementsApi(api).publish(saved.id);
      return saved;
    },
    onSuccess: async (saved) => {
      await queries.invalidateQueries({ queryKey: ['announcements'] });
      toast.show({ tone: 'success', title: t(`announcement.saved.${saved.status}`) });
      onDone(saved);
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (audience !== 'all' && audience !== 'family' && !targetId) {
      setError(t('announcement.error.target'));
      return;
    }
    if (audience === 'family' && !family) {
      setError(t('announcement.error.family'));
      return;
    }
    if (mode === 'schedule' && !scheduledAt) {
      setError(t('announcement.error.schedule'));
      return;
    }
    save.mutate();
  };
  const targets =
    audience === 'sede'
      ? (sedes.data?.items ?? [])
      : audience === 'grade'
        ? (grades.data?.items ?? [])
        : audience === 'group'
          ? (groups.data?.items ?? [])
          : [];
  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Input
        label={t('announcement.field.title')}
        required
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <div>
        <label htmlFor="announcement-body" className="block text-sm font-medium text-gray-900">
          {t('announcement.field.body')}
        </label>
        <textarea
          id="announcement-body"
          required
          rows={6}
          className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </div>
      <fieldset disabled={frozen} className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('announcement.field.audience')}
          value={audience}
          onChange={(event) => {
            setAudience(event.target.value as AnnouncementAudience);
            setTargetId('');
            setFamily(null);
          }}
          options={AUDIENCES.map((value) => ({
            value,
            label: t(`announcement.audience.${value}`),
          }))}
        />
        {audience === 'sede' || audience === 'grade' || audience === 'group' ? (
          <Select
            label={t(`announcement.target.${audience}`)}
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
            options={[
              { value: '', label: t('assignments.pick') },
              ...targets.map((row) => ({
                value: (row as { id: string }).id,
                label: (row as { name: string }).name,
              })),
            ]}
          />
        ) : null}
        {audience === 'family' ? (
          <div className="space-y-1">
            <Input
              label={t('announcement.target.family')}
              help={t('announcement.target.family.help')}
              value={familySearch}
              onChange={(event) => {
                setFamilySearch(event.target.value);
                setFamily(null);
              }}
            />
            {family ? (
              <p className="text-sm text-success-700">
                {t('link.selected', { name: family.label })}
              </p>
            ) : null}
            {!family && students.data && students.data.items.length > 0 ? (
              <ul className="max-h-32 divide-y divide-gray-100 overflow-auto rounded-control border border-gray-200 text-sm">
                {students.data.items.map((student) => (
                  <li key={student.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50"
                      onClick={() =>
                        setFamily({
                          studentId: student.id,
                          label:
                            `${student.givenName ?? ''} ${student.familyName ?? ''}${student.groupName ? ` · ${student.groupName}` : ''}`.trim(),
                        })
                      }
                    >
                      {student.givenName} {student.familyName}
                      <span className="ml-2 text-xs text-gray-500">{student.groupName ?? ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </fieldset>
      {!current ? (
        <Input
          label={t('announcement.field.attachments')}
          type="file"
          multiple
          onChange={(event) => setFiles([...(event.target.files ?? [])])}
          help={t('announcement.field.attachments.help')}
        />
      ) : null}
      {!frozen ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('announcement.field.mode')}
            value={mode}
            onChange={(event) => setMode(event.target.value as 'now' | 'schedule' | 'draft')}
            options={[
              { value: 'draft', label: t('announcement.mode.draft') },
              { value: 'now', label: t('announcement.mode.now') },
              { value: 'schedule', label: t('announcement.mode.schedule') },
            ]}
          />
          {mode === 'schedule' ? (
            <Input
              label={t('announcement.field.scheduledAt')}
              type="datetime-local"
              required
              value={scheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
            />
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t('announcement.frozen')}</p>
      )}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {frozen ? t('common.save') : t(`announcement.submit.${mode}`)}
        </Button>
      </div>
    </form>
  );
};
